import { type FocusEvent, type FormEvent, useEffect, useRef, useState } from 'react';
import type { PreferredLogin } from './types';
import { isAustrianPhoneNumber, isSpanishPhoneNumber, validate, ValidationType } from './utils/validators';
import { FormattedMessage } from 'react-intl';
import { SecondScreenInput } from './components/SecondScreenInput';
import { getPhonePrefixFromDriverCardCountry } from './utils/phonePrefix';
import type { DriverCardCountry } from './utils/driverCardCountry';
import { type DriverSubmissionResult, isFirstScreenValidationError } from './utils/requests';

interface Props {
    email: string;
    phone: string;
    preferredLogin: PreferredLogin;
    setEmail: (input: string) => void;
    setPhone: (input: string) => void;
    setPreferredLogin: (input: PreferredLogin) => void;
    setStep: (input: number) => void;
    submit: () => Promise<DriverSubmissionResult>;
    setFirstScreenErrorCode: (input: number | undefined) => void;
    driverCardCountry: DriverCardCountry;
    pending: boolean;
}

const validateEmail = (email: string): boolean => validate({ type: ValidationType.Email, value: email });

const validatePhoneFormat = (phone: string): boolean => validate({ type: ValidationType.PhoneFormat, value: phone });

const validatePhoneCountryCode = (phone: string): boolean =>
    validate({ type: ValidationType.PhoneCountryCode, value: phone });

export const SecondScreen = (props: Props) => {
    const {
        email,
        phone,
        preferredLogin,
        setEmail,
        setPhone,
        setPreferredLogin,
        setStep,
        submit,
        setFirstScreenErrorCode,
        pending,
        driverCardCountry,
    } = props;

    const [errorCode, setErrorCode] = useState<undefined | number>();
    const switchingLogin = useRef(false);
    const [touched, setTouched] = useState({ email: false, phone: false });

    const [submitted, setSubmitted] = useState(false);

    const phonePrefix = getPhonePrefixFromDriverCardCountry(driverCardCountry);
    const normalizedEmail = email.trim();
    const normalizedPhone = phone.trim();
    const inputErrors = {
        email: !validateEmail(normalizedEmail),
        phoneFormat: !normalizedPhone || !validatePhoneFormat(normalizedPhone),
        phoneCountryCode: !validatePhoneCountryCode(normalizedPhone),
    };
    const isBlockedPhone = isSpanishPhoneNumber(normalizedPhone) || isAustrianPhoneNumber(normalizedPhone);
    const isSelectedLoginValid =
        preferredLogin === 'email'
            ? !inputErrors.email
            : !inputErrors.phoneFormat && !inputErrors.phoneCountryCode && !isBlockedPhone;

    const handleBlur = (inputType: PreferredLogin, event: FocusEvent<HTMLInputElement>) => {
        const isSwitchingLogin = switchingLogin.current;
        switchingLogin.current = false;
        const nextTarget = event.relatedTarget;
        if (
            isSwitchingLogin ||
            inputType !== preferredLogin ||
            (nextTarget instanceof HTMLElement &&
                nextTarget.matches('[name="loginChoice"], #email-input, #phone-input, [data-login-focus-helper]'))
        ) {
            return;
        }
        setTouched(current => ({ ...current, [inputType]: true }));
        if (inputType === 'email') {
            setEmail(normalizedEmail);
        } else {
            setPhone(normalizedPhone);
        }
    };

    const handleSetEmail = (event: FormEvent<HTMLInputElement>) => {
        setEmail(event.currentTarget.value);
    };
    const handleSetPhone = (event: FormEvent<HTMLInputElement>) => {
        if (event.currentTarget.value.trim().length <= 16) {
            // Enforce the limit after trimming so pasted whitespace cannot truncate the number.
            setPhone(event.currentTarget.value);
        }
    };

    const handleSelectEmail = () => {
        switchingLogin.current = false;
        if (preferredLogin === 'email') {
            return;
        }
        setTouched({ email: false, phone: false });
        setSubmitted(false);
        setPreferredLogin('email');
        setPhone('');

        try {
            setFocus(document.getElementById('email-input') as HTMLInputElement);
        } catch (e) {
            console.error('No input element for phone number found');
        }
    };

    const handleSelectPhone = () => {
        switchingLogin.current = false;
        if (preferredLogin === 'phone') {
            return;
        }
        setTouched({ email: false, phone: false });
        setSubmitted(false);
        setPreferredLogin('phone');
        setEmail('');
        if (phone === '') {
            setPhone(phonePrefix);
        }

        try {
            setFocus(document.getElementById('phone-input') as HTMLInputElement);
        } catch (e) {
            console.error('No input element for phone number found');
        }
    };

    useEffect(() => {
        try {
            setFocus(document.getElementById(`${preferredLogin}-input`) as HTMLInputElement, 250);
        } catch (e) {
            console.error('No input element for phone number found');
        }
    }, []);

    const handleSuccessfulSubmit = () => {
        setErrorCode(undefined);
        setFirstScreenErrorCode(undefined);
        setStep(3);
    };

    const handleSubmitError = (response: DriverSubmissionResult) => {
        if (isFirstScreenValidationError(response)) {
            setErrorCode(undefined);
            setFirstScreenErrorCode(response.status);
            setStep(1);
            return;
        }

        setErrorCode(response.status);
    };

    const handleConfirm = async () => {
        if (pending) {
            return;
        }
        setSubmitted(true);
        if (isSelectedLoginValid) {
            const response = await submit();

            if (response.status === 201) {
                handleSuccessfulSubmit();
            } else {
                handleSubmitError(response);
            }
        } else {
            document.getElementById(`${preferredLogin}-input`)?.focus();
        }
    };

    const handleBack = () => {
        setStep(1);
    };

    return (
        <>
            <div className={'text-size-16'}>
                <span className={'uikit-icon icon-number-2 text-color-darker text-size-400pct'} />
            </div>
            <div className={'text-size-h4 text-medium text-center'}>
                <FormattedMessage id={'driverRegistration.page2.title'} />
            </div>
            <div
                className={
                    'flex-1-1 width-100pct margin-top-15pct display-flex flex-column justify-content-center align-items-center'
                }
            >
                <SecondScreenInput
                    value={email}
                    inputType={'email'}
                    showValidation={preferredLogin === 'email' && (touched.email || submitted)}
                    handleBlur={event => handleBlur('email', event)}
                    inputErrors={inputErrors}
                    preferredLogin={preferredLogin}
                    handleRadioSelection={handleSelectEmail}
                    handleSelectionStart={() => {
                        switchingLogin.current = !pending && preferredLogin !== 'email';
                    }}
                    handleInput={handleSetEmail}
                    isPending={pending}
                />

                <SecondScreenInput
                    value={phone}
                    inputType={'phone'}
                    showValidation={preferredLogin === 'phone' && (touched.phone || submitted)}
                    handleBlur={event => handleBlur('phone', event)}
                    inputErrors={inputErrors}
                    preferredLogin={preferredLogin}
                    handleRadioSelection={handleSelectPhone}
                    handleSelectionStart={() => {
                        switchingLogin.current = !pending && preferredLogin !== 'phone';
                    }}
                    handleInput={handleSetPhone}
                    isPending={pending}
                    driverCardCountry={driverCardCountry}
                />
            </div>
            <div>
                {pending ? (
                    <div className={'text-color-info text-center'}>
                        <FormattedMessage id={'driverRegistration.page2.pendingRequest'} />
                    </div>
                ) : errorCode ? (
                    <div className={'alert alert-dismissible alert-danger text-center'} data-cy={'page-2-error'}>
                        <FormattedMessage id={`driverRegistration.page2.error.${errorCode}`} />
                    </div>
                ) : (
                    <div>&nbsp;</div>
                )}
            </div>
            <div className={'btn-toolbar margin-top-10pct margin-bottom-20'}>
                <button
                    type={'button'}
                    className={'btn btn-lg btn-default btn-outline margin-right-15'}
                    onClick={handleBack}
                >
                    <FormattedMessage id={'driverRegistration.backButtonText'} />
                </button>
                <button
                    type={'button'}
                    className={`btn btn-lg btn-primary${pending ? ' btn-loading' : ''}`}
                    onClick={handleConfirm}
                    disabled={pending || !isSelectedLoginValid}
                    data-cy={'page-2-next'}
                >
                    <FormattedMessage id={'driverRegistration.confirmButtonText'} />
                </button>
            </div>
        </>
    );
};

const setFocus = (targetInput: HTMLInputElement, timeOut = 100) => {
    // Fix focus for iOS: https://stackoverflow.com/a/55652503
    // create invisible dummy input to receive the focus first
    const fakeInput = document.createElement('input');
    fakeInput.setAttribute('type', 'text');
    fakeInput.setAttribute('data-login-focus-helper', '');
    fakeInput.readOnly = true;
    fakeInput.style.position = 'absolute';
    fakeInput.style.opacity = '0';
    fakeInput.style.height = '0';
    fakeInput.style.fontSize = '16px'; // disable auto zoom

    // you may need to append to another element depending on the browser's auto
    // zoom/scroll behavior
    document.body.prepend(fakeInput);

    // focus so that subsequent async focus will work
    fakeInput.focus();

    setTimeout(() => {
        // now we can focus on the target input
        targetInput.focus();

        // cleanup
        fakeInput.remove();
    }, timeOut);
};
